import {
  IconNameEnum,
  useHotelsStyles,
  useModuleBEM,
} from "@b2bportal/core-themes";
import {
  CoreHotelsComponent,
  type PlatformComponentProps,
} from "@b2bportal/core-types";
import type { GuestsSelection } from "@b2bportal/lodging-api";
import {
  ChildAgePicker,
  GuestCountPicker,
  RoomCountPicker,
  useGuestPicker,
} from "@tenant/hotels";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTrigger,
  Icon,
} from "@tenant/ui";
import clsx from "clsx";

import defaultStyles from "./GuestPicker.module.scss";

export interface GuestPickerComponentProps extends PlatformComponentProps {
  onGuestSelectionHandler: (guests: GuestsSelection, rooms?: number) => void;
  guests: GuestsSelection;
  rooms?: number;
  onRoomsSelectionHandler?: (rooms: number) => void;
  config?: {
    enableRoomSelection: boolean;
  };
}

export const GuestPicker = ({
  className,
  children,
  ...rest
}: GuestPickerComponentProps) => {
  const COMPONENT_KEY = CoreHotelsComponent.GuestPicker;
  const styles = useHotelsStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);

  const { context, handlers, text } = useGuestPicker(rest);
  return (
    <div className={clsx(block, className)}>
      <Dialog
        onOpenChange={(open) => {
          handlers.onOpenChange(open);
        }}
        modal={false}
      >
        <DialogTrigger asChild>
          <button className={cn("guest-button")}>
            <Icon iconName={IconNameEnum.guest} />
            <span className={cn("guest-button-label-container")}>
              <span className={cn("guest-button-floating-label")}>
                {text.buttonFloatingLabel}
              </span>
              <span>{text.buttonLabel}</span>
            </span>
          </button>
        </DialogTrigger>
        <DialogContent
          showOverlay={true}
          title={text.modalTitle}
          className={cn("guest-dialog")}
          fullScreen={context.showGuestPickerAsFullscreen}
          bottomFixedContent={
            <DialogClose
              className={cn("apply-button")}
              disabled={!context.isGuestPickerValid}
              onClick={() =>
                handlers.onGuestSelection({
                  adults: context.adultCount,
                  children: context.childAgeArr,
                })
              }
            >
              {text.applyButtonLabel}
            </DialogClose>
          }
        >
          <div className={cn("dialog-content")}>
            <GuestCountPicker
              title={text.adultTitle}
              subtitle={text.adultSubtitle}
              minimumCount={context.minimumAdultCount}
              count={context.adultCount}
              setCount={handlers.onAdultChange}
              maximumReached={context.maximumReached}
            />
            <GuestCountPicker
              title={text.childCountTitle}
              subtitle={text.childCountSubtitle}
              minimumCount={0}
              count={context.childCount}
              setCount={(count) => handlers.setChildCount(count)}
              maximumReached={context.maximumReached}
            />
            {Array.from({ length: context.childCount }).map((_, index) => {
              return (
                <ChildAgePicker
                  key={index}
                  onAgeSelectHandler={(age: number) => {
                    handlers.updateChildAge(index, age);
                  }}
                  childAge={context.childAgeArr[index]}
                  label={text.childAgeLabel(index)}
                />
              );
            })}
            {context.enableRoomSelection === true ? (
              <RoomCountPicker
                title={text.roomTitle}
                subtitle={text.roomSubtitle}
                count={context.roomCount}
                setCount={(count) => handlers.onRoomsChange(count)}
                maximumReached={context.maximumRoomsReached}
              />
            ) : null}
            {children}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
